import { createContext, use, useLayoutEffect, useRef } from "react";
import { Blocks } from "loading-dev";

/**
 * The size a loader takes where it sits beside a control's label rather than on its own. `Button`
 * provides it, so a pending button's loader is no taller than the button's text.
 */
export const LoaderSizeContext = createContext<number | undefined>(undefined);

type LoaderProps = {
  /**
   * Width and height in pixels. Inside a button it follows the button's text; elsewhere inline
   * uses keep the 16px default so it sits in a line of text.
   */
  size?: number | undefined;
  className?: string | undefined;
};

/**
 * The product's one progress indicator. Every pending state renders this, so the effect is chosen
 * here and nowhere else: swapping `Blocks` for another `loading-dev` indicator, or for something
 * of our own, changes every loading state at once.
 *
 * Every loader keeps the document's rhythm rather than its own: its sweep starts at the page's
 * time origin, not when it mounted. A loader that replaces another mid-load (one route's waiting
 * screen handing over to the next) carries on where the last one was instead of starting over.
 *
 * Decorative; pair it with text describing what is pending. It inherits `currentColor` and holds
 * still under reduced motion.
 */
export function Loader({ size, className }: LoaderProps) {
  const inherited = use(LoaderSizeContext);
  const pixels = size ?? inherited ?? 16;
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    // Not every DOM has the Web Animations API (test environments); there the sweep simply starts.
    for (const animation of ref.current?.getAnimations?.({ subtree: true }) ?? []) {
      animation.startTime = 0;
    }
  }, []);
  return (
    <span ref={ref} className={className} data-slot="loader" aria-hidden="true">
      <Blocks size={pixels} />
    </span>
  );
}
